import React, { useRef, useState } from "react";
import { Volume2Icon } from "lucide-react";
import { trackOnce } from "../utils/tracking";

// ─── Configuración ───────────────────────────────────────────────
// MP4 alojado en GHL Media Storage.
export const VIDEO_URL = "https://assets.cdn.filesafe.space/Z34SpQXLnqpWmkXNOFgC/media/6ac54978c062e4fb052c5bfd.mp4";
export const VIDEO_LABEL = "Toca para escuchar · 40 s";
// ─────────────────────────────────────────────────────────────────

type VideoPlayerProps = {
  /** Kept for compatibility (hero = "poster", desktop section = "embed"). Both play a muted preview. */
  mode: "embed" | "poster";
};

/**
 * Muted, looping preview plays automatically (like a live stream).
 * Tapping restarts the video from 0:00 with sound and shows the controls.
 * Meta events (VideoPlay / Video50 / VideoComplete) only fire after the user taps.
 */
export function VideoPlayer(_props: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [active, setActive] = useState(false);
  const activate = () => {
    const v = videoRef.current;
    if (!v) return;
    v.loop = false;
    v.muted = false;
    v.currentTime = 0;
    void v.play().catch(() => undefined);
    setActive(true);
    trackOnce("VideoPlay", {}, {
      custom: true
    });
  };
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!active || !v || !(v.duration > 0)) return;
    if (v.currentTime / v.duration >= 0.5) trackOnce("Video50", {}, {
      custom: true
    });
  };
  const handleEnded = () => {
    if (!active) return;
    trackOnce("Video50", {}, {
      custom: true
    });
    trackOnce("VideoComplete", {}, {
      custom: true
    });
  };
  if (!VIDEO_URL) return null;
  return <div className="relative aspect-video w-full overflow-hidden rounded-[3px] border border-gold/70 bg-surface" data-id="element-371" data-mp="2fgepmjltlqte">
      <video ref={videoRef}
    // #t=0.1 paints the first frame as a thumbnail even if autoplay is blocked.
    src={`${VIDEO_URL}#t=0.1`} title="Mensaje del equipo — Studio Web VIP" autoPlay muted loop playsInline preload="auto" controls={active} controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} onTimeUpdate={handleTimeUpdate} onEnded={handleEnded}
    // object-cover fills the whole frame (no black bars).
    className="absolute inset-0 h-full w-full object-cover" data-id="element-372" data-mp="1hzpt95fn3gre" />

      {!active && <button type="button" onClick={activate} aria-label="Reproducir el video con sonido — 40 segundos"
    // Transparent overlay: the video stays fully visible; only the button sits on top.
    className="group absolute inset-0 flex flex-col items-center justify-center gap-3 bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold" data-id="element-373" data-mp="rz6tsn2zeh2r">
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gold text-ink shadow-lg transition-transform duration-200 ease-out group-active:scale-[0.97] motion-safe:animate-[svip-pulse_2s_ease-out_infinite]" data-id="element-374" data-mp="2cea9kkjvk2r8">
            <Volume2Icon className="h-7 w-7" aria-hidden="true" data-id="element-375" data-mp="3lc0auesppbqt" />
          </span>
          <span className="rounded-[3px] bg-black/55 px-3 py-1.5 font-ui text-[12px] font-bold uppercase tracking-[0.18em] text-gold backdrop-blur-sm" data-id="element-376" data-mp="3qajzimyox1mv">
            {VIDEO_LABEL}
          </span>
        </button>}

      <style data-id="element-377" data-mp="lwv6sq84lskj">{`@keyframes svip-pulse{0%{box-shadow:0 0 0 0 rgba(245,184,0,.55)}70%{box-shadow:0 0 0 18px rgba(245,184,0,0)}100%{box-shadow:0 0 0 0 rgba(245,184,0,0)}}`}</style>
    </div>;
}